import * as React from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { MaskedPhoneInput } from '../PhoneNumberTextField/PhoneNumberTextField';
import { TextField, TextFieldProps } from '../TextField/TextField';

export interface TextFieldFormItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  // -- UseControllerProps -- //
  name: UseControllerProps<TFieldValues, TName>['name'];
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * @default null
   */
  defaultValue?: UseControllerProps['defaultValue'];

  // -- TextFieldProps -- //
  variant?: TextFieldProps['variant'];
  disabled?: TextFieldProps['disabled'];
  slotProps?: TextFieldProps['slotProps'];
  label: TextFieldProps['label'];
  /**
   * @default 1
   */
  maxRows?: TextFieldProps['maxRows'];
  /**
   * @default 1
   */
  minRows?: TextFieldProps['minRows'];
  multiline?: TextFieldProps['multiline'];
  fullWidth?: TextFieldProps['fullWidth'];
  onEnter?: TextFieldProps['onEnter'];
  onChange?: TextFieldProps['onChange'];
  required?: TextFieldProps['required'];
  sx?: TextFieldProps['sx'];
}

/**
 * Convenience wrapper component for simple text input form fields.
 */
export const TextFieldFormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: TextFieldFormItemProps<TFieldValues, TName>
) => {
  const {
    name,
    control,
    defaultValue,
    variant,
    disabled,
    slotProps,
    label,
    maxRows,
    minRows,
    multiline,
    fullWidth,
    onEnter,
    required,
    sx,
    onChange: onChangeFromProps
  } = props;

  const {
    field: { ref, ...field },
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue: defaultValue ?? null
  });

  const { onChange, value, ...restOfField } = field;

  return (
    <TextField
      disabled={disabled}
      error={!!error}
      helperText={error?.message ?? ''}
      fullWidth={fullWidth !== undefined ? fullWidth : true}
      slotProps={slotProps}
      inputRef={ref}
      label={label}
      required={required}
      margin='dense'
      variant={variant}
      maxRows={maxRows || 1}
      minRows={minRows || 1}
      multiline={multiline}
      sx={sx}
      onChange={(e) => {
        onChange(e);
        if (onChangeFromProps) onChangeFromProps(e);
      }}
      onEnter={onEnter}
      value={value || null}
      {...restOfField}
    />
  );
};

export const PhoneNumberTextFieldFormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: TextFieldFormItemProps<TFieldValues, TName>
) => {
  const { slotProps, ...restProps } = props;

  return (
    <TextFieldFormItem
      slotProps={{
        input: {
          inputComponent: MaskedPhoneInput
        },
        ...slotProps
      }}
      {...restProps}
    />
  );
};
